/* Native Shopify product controls. Shopify remains the price and inventory authority. */
(() => {
  if (window.fnProductBooted) return;
  window.fnProductBooted = true;
  const roots = new WeakMap();
  const rootPath = () => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
  class FnProduct {
    constructor(root) {
      this.root = root;
      this.abort = new AbortController();
      this.signal = this.abort.signal;
      try { this.data = JSON.parse(root.querySelector('[data-fn-product-data]').textContent); } catch (_) { return; }
      this.form = root.querySelector('[data-fn-form]');
      this.externalPurchase = root.dataset.externalPurchase === 'true';
      if (this.externalPurchase) this.form?.querySelector('[data-fn-variant-id]')?.removeAttribute('disabled');
      this.variants = this.data.variants || [];
      this.baseId = String(this.data.selectedVariant || this.variants[0]?.id || '');
      this.offers = [...root.querySelectorAll('[data-fn-offer]')];
      this.offer = null;
      this.quantity = 1;
      this.planId = new URL(window.location.href).searchParams.get('selling_plan') || '';
      this.purchase = root.querySelector('[data-fn-purchase]')?.dataset.defaultPurchase || 'once';
      if (this.planId) this.purchase = 'subscription';
      this.preview = root.dataset.referencePreview === 'true';
      this.editor = root.dataset.designMode === 'true';
      this.busy = false;
      this.money = new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: this.data.currency || 'USD' });
      const listen = (element, type, callback, options = {}) => element?.addEventListener(type, callback, { ...options, signal: this.signal });
      this.listen = listen;
      listen(root, 'change', event => this.change(event));
      listen(root, 'click', event => this.click(event));
      // App purchases run at the final bubbling stage so form/document app handlers can own the submit first.
      listen(this.externalPurchase ? window : this.form, 'submit', event => { if (event.target === this.form) this.submit(event); });
      listen(root, 'shopify:block:select', event => {
        const slide = event.target.closest('[data-fn-slide]');
        if (slide) this.goTo(Number(slide.dataset.index), false);
        const details = event.target.closest('details');
        if (details) details.open = true;
      });
      this.setupGallery();
      this.setupSticky();
      this.offers.forEach(offer => {
        const variant = this.offerVariant(offer);
        if (!this.offer && this.canBuy(variant, Number(offer.dataset.quantity || 1))) this.offer = offer;
      });
      this.sync(true);
    }
    variant(id = this.baseId) { return this.variants.find(item => String(item.id) === String(id)); }
    offerVariant(offer) { return this.variant(offer?.dataset.variantId?.trim() || this.baseId); }
    get activeVariant() { return this.externalPurchase ? this.externalSelection().variant : this.offer ? this.offerVariant(this.offer) : this.variant(); }
    externalSelection() {
      const fields = new FormData(this.form);
      const lines = new Map();
      for (const [name, value] of fields) {
        const match = name.match(/^items\[(\d+)\]\[(id|quantity|selling_plan)\]$/);
        if (match) {
          if (!lines.has(match[1])) lines.set(match[1], {});
          lines.get(match[1])[match[2]] = String(value);
        }
      }
      const lastValue = name => fields.getAll(name).map(String).filter(value => value.trim()).pop() || '';
      const entry = lines.size ? [...lines.entries()].find(([, item]) => this.variant(item.id)) : null;
      const line = lines.size ? entry?.[1] : { id: lastValue('id'), quantity: lastValue('quantity'), selling_plan: lastValue('selling_plan') };
      const variant = line ? this.variant(line.id) : null;
      return { fields, indexed: lines.size > 0, variant, quantity: Number(line?.quantity || 1), planId: line?.selling_plan || '' };
    }
    amount(cents) { return this.money.format(Number(cents || 0) / 100); }
    text(selector, text) { this.root.querySelectorAll(selector).forEach(node => { node.textContent = text; }); }
    canBuy(variant, quantity) {
      if (!variant?.available || !Number.isSafeInteger(quantity) || quantity < 1) return false;
      const rule = variant.quantityRule || {};
      if (quantity < Number(rule.min || 1) || (rule.max && quantity > Number(rule.max))) return false;
      if ((quantity - Number(rule.min || 1)) % Number(rule.increment || 1) !== 0) return false;
      return !(variant.inventoryManagement && variant.inventoryPolicy === 'deny' && Number(variant.inventory) < quantity);
    }
    unitPrice(variant, quantity, plan = null) {
      if (plan) return Number(plan.price);
      let price = Number(variant?.price || 0);
      [...(variant?.breaks || [])].sort((a, b) => a.minimum - b.minimum).forEach(item => { if (quantity >= item.minimum) price = Number(item.price); });
      return price;
    }
    change(event) {
      const option = event.target.closest('[data-fn-option]');
      if (option) {
        const selected = [...this.root.querySelectorAll('[data-fn-option]')].sort((a, b) => Number(a.dataset.fnOption) - Number(b.dataset.fnOption)).map(node => node.value);
        const match = this.variants.find(variant => variant.options.every((value, index) => value === selected[index]));
        this.baseId = match ? String(match.id) : '';
        if (this.offer?.dataset.variantId) this.offer = this.offers.find(node => !node.dataset.variantId && this.canBuy(this.offerVariant(node), Number(node.dataset.quantity))) || null;
        this.sync(true);
        return;
      }
      const offer = event.target.closest('[data-fn-offer]');
      if (offer) {
        this.offer = offer;
        const mapped = this.offerVariant(offer);
        if (offer.dataset.variantId && mapped) {
          this.baseId = String(mapped.id);
          this.root.querySelectorAll('[data-fn-option]').forEach(node => { node.value = mapped.options[Number(node.dataset.fnOption)]; });
        }
        this.sync(true);
        return;
      }
      if (event.target.matches('input[name^="fn-purchase-"]')) { this.purchase = event.target.value; this.sync(); }
      if (event.target.matches('[data-fn-plan-select]')) { this.planId = event.target.value; this.purchase = 'subscription'; this.sync(); }
      if (this.externalPurchase) queueMicrotask(() => { if (!this.signal.aborted) this.sync(); });
    }
    syncExternal(updateMedia = false) {
      const selection = this.externalSelection();
      const variant = selection.variant || this.variant();
      this.root.querySelectorAll('[data-fn-add]').forEach(button => {
        button.disabled = !variant?.available || this.busy;
        button.querySelector('[data-fn-add-label]').textContent = !variant ? 'Unavailable' : variant.available ? button.dataset.label : button.dataset.soldOut;
      });
      if (updateMedia && variant?.mediaId) {
        const slide = this.root.querySelector(`[data-media-id="${variant.mediaId}"]`);
        if (slide) this.goTo(Number(slide.dataset.index), false);
      }
    }
    sync(updateMedia = false) {
      if (this.externalPurchase) { this.syncExternal(updateMedia); return; }
      const external = this.externalPurchase ? this.externalSelection() : null;
      const variant = external ? external.variant : this.activeVariant;
      this.root.querySelectorAll('[data-fn-option-label]').forEach(label => { label.textContent = variant?.options?.[Number(label.dataset.fnOptionLabel)] || ''; });
      this.quantity = external ? external.quantity : this.offer ? Number(this.offer.dataset.quantity || 1) : Number(variant?.quantityRule?.min || 1);
      this.offers.forEach(offer => {
        const item = this.offerVariant(offer), quantity = Number(offer.dataset.quantity || 1), units = Number(offer.dataset.units || 1);
        const input = offer.querySelector('input');
        input.disabled = !this.canBuy(item, quantity);
        input.checked = offer === this.offer;
        const previewPrice = Number(offer.dataset.previewPrice || 0);
        const displayPrice = this.preview && previewPrice > 0 ? previewPrice * 100 : this.unitPrice(item, quantity) / Math.max(1, units);
        offer.querySelector('[data-fn-offer-price]').textContent = item || this.preview ? this.amount(displayPrice) : 'Unavailable';
      });
      this.text('[data-fn-offer-label]', this.offer?.dataset.label || '');
      const plans = variant?.plans || [];
      if (external) {
        // Only the app/customer may choose an external selling plan.
        this.planId = external.planId;
        this.purchase = external.planId ? 'subscription' : 'once';
      } else {
        if (!plans.some(plan => String(plan.id) === String(this.planId))) this.planId = plans.length ? String(plans[0].id) : '';
        if (!plans.length) this.purchase = 'once';
        if (variant?.requiresPlan && plans.length) this.purchase = 'subscription';
      }
      const selector = this.root.querySelector('[data-fn-plan-select]');
      if (selector) {
        const key = plans.map(plan => `${plan.id}:${plan.name}`).join('|');
        if (selector.dataset.optionsKey !== key) {
          selector.replaceChildren(...plans.map(plan => { const option = document.createElement('option'); option.value = String(plan.id); option.textContent = plan.name; return option; }));
          selector.dataset.optionsKey = key;
        }
        selector.value = this.planId;
        selector.disabled = !plans.length;
      }
      this.root.querySelectorAll('[data-fn-sub-card],[data-fn-sub-heading]').forEach(node => { node.hidden = !plans.length; });
      this.root.querySelectorAll('[data-fn-once-card]').forEach(node => { node.hidden = !!variant?.requiresPlan; });
      this.root.querySelectorAll('[data-fn-plan-note]').forEach(node => { node.hidden = !!plans.length; });
      this.root.querySelectorAll('input[name^="fn-purchase-"]').forEach(input => { input.checked = input.value === this.purchase; });
      const plan = plans.find(item => String(item.id) === this.planId);
      const selectedPlan = this.purchase === 'subscription' ? plan : null;
      const unit = this.unitPrice(variant, this.quantity, selectedPlan), total = unit * this.quantity;
      const once = this.unitPrice(variant, this.quantity) * this.quantity;
      const compare = Math.max(Number(variant?.compare || 0), variant?.price || 0) * this.quantity;
      this.text('[data-fn-price]', this.amount(total));
      this.text('[data-fn-sticky-price]', this.amount(total));
      this.text('[data-fn-compare]', this.amount(compare));
      this.root.querySelectorAll('[data-fn-compare]').forEach(node => { node.hidden = compare <= total; });
      this.text('[data-fn-once-price]', this.amount(once));
      this.text('[data-fn-sub-price]', this.amount(Number(plan?.price || 0) * this.quantity));
      this.text('[data-fn-sub-compare]', this.amount(once));
      this.root.querySelectorAll('[data-fn-sub-compare]').forEach(node => { node.hidden = !plan || Number(plan.price) * this.quantity >= once; });
      const percent = compare > total && compare > 0 ? Math.round((compare - total) / compare * 100) : 0;
      this.root.querySelectorAll('[data-fn-saving]').forEach(node => { node.hidden = !percent; node.textContent = `SAVE ${percent}%`; });
      const planPercent = plan && once > 0 ? Math.round((once - Number(plan.price) * this.quantity) / once * 100) : 0;
      this.root.querySelectorAll('[data-fn-plan-saving]').forEach(node => { node.hidden = planPercent <= 0; node.textContent = `You Save ${planPercent}%`; });
      this.text('[data-fn-plan-heading-saving]', planPercent > 0 ? `${planPercent}%` : '');
      this.text('[data-fn-sub-benefit-saving-text]', planPercent > 0 ? `Save ${planPercent}% on every order` : '');
      this.root.querySelectorAll('[data-fn-sub-benefit-saving]').forEach(node => { node.hidden = planPercent <= 0; });
      const planUI = this.root.querySelector('[data-fn-purchase]');
      const needsPlan = !!variant?.requiresPlan && (!selectedPlan || (!external && !planUI));
      const invalidPlan = !!external?.planId && !selectedPlan;
      const validQuantity = this.canBuy(variant, this.quantity);
      if (!external) {
        const idField = this.root.querySelector('[data-fn-variant-id]');
        if (idField) { idField.value = variant?.id || ''; idField.disabled = !variant; }
        const quantityField = this.root.querySelector('[data-fn-quantity]');
        if (quantityField) quantityField.value = String(this.quantity);
        const planField = this.root.querySelector('[data-fn-selling-plan]');
        if (planField) { planField.value = selectedPlan && planUI ? String(selectedPlan.id) : ''; planField.disabled = !selectedPlan || !planUI; }
      }
      this.root.querySelectorAll('[data-fn-add]').forEach(button => {
        button.disabled = !variant || !variant.available || this.busy;
        button.querySelector('[data-fn-add-label]').textContent = !variant ? 'Unavailable' : !variant.available ? button.dataset.soldOut : external ? button.dataset.label : needsPlan || invalidPlan ? 'Choose purchase option' : !validQuantity ? 'Choose quantity' : button.dataset.label;
      });
      this.root.querySelectorAll('[data-fn-stock]').forEach(node => {
        node.dataset.available = String(!!variant?.available);
        const backorder = variant?.inventoryManagement && variant.inventoryPolicy === 'continue' && Number(variant.inventory) <= 0;
        node.textContent = !variant?.available ? node.dataset.soldOut : backorder ? node.dataset.preorder : node.dataset.inStock;
      });
      if (!needsPlan && !invalidPlan) this.showError('');
      if (updateMedia && variant?.mediaId) {
        const slide = this.root.querySelector(`[data-media-id="${variant.mediaId}"]`);
        if (slide) this.goTo(Number(slide.dataset.index), false);
      }
      if (variant && !this.editor && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        if (selectedPlan) url.searchParams.set('selling_plan', selectedPlan.id); else url.searchParams.delete('selling_plan');
        history.replaceState({}, '', url);
      }
      this.root.dispatchEvent(new CustomEvent('fn:variant:change', { bubbles: true, detail: { variant, quantity: this.quantity, sellingPlan: selectedPlan } }));
    }
    showError(message) { this.root.querySelectorAll('[data-fn-error]').forEach(node => { node.hidden = !message; node.textContent = message; }); }
    async submitExternal() {
      // Kaching owns quantities, prices, purchase type, and gifts. Submit its actual Shopify form.
      this.busy = true;
      this.showError('');
      this.root.querySelectorAll('[data-fn-add]').forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      try {
        const body = new FormData(this.form);
        if (!body.has('sections')) body.set('sections', 'cart-icon-bubble');
        if (!body.has('sections_url')) body.set('sections_url', window.location.pathname);
        const response = await fetch(rootPath() + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'The product could not be added. Please try again.');
        const bubble = result.sections?.['cart-icon-bubble'];
        if (bubble) {
          const parsed = new DOMParser().parseFromString(bubble, 'text/html');
          const fresh = parsed.querySelector('#cart-icon-bubble'), existing = document.querySelector('#cart-icon-bubble');
          if (fresh && existing) existing.innerHTML = fresh.innerHTML;
        }
        this.root.querySelectorAll('[data-fn-cart-status]').forEach(node => { node.hidden = false; node.textContent = 'Added to your cart.'; });
        this.root.querySelectorAll('[data-fn-cart-link]').forEach(node => { node.hidden = false; });
        document.dispatchEvent(new CustomEvent('fn:cart:updated', { detail: result }));
        // The cart app handles its own drawer through its configured Shopify add-to-cart integration.
      } catch (error) {
        this.showError(error.message || 'Unable to add this item. Please check your connection and try again.');
      } finally {
        this.busy = false;
        this.lastStickyTrigger = null;
        this.root.querySelectorAll('[data-fn-add]').forEach(button => button.removeAttribute('aria-busy'));
        this.syncExternal();
      }
    }
    async submit(event) {
      // Respect an app that has already claimed this native submit event.
      if (event.defaultPrevented) return;
      event.preventDefault();
      if (this.busy) return;
      if (this.editor) { this.showError('Theme editor preview: open the storefront preview to test adding the real Shopify product.'); return; }
      if (this.externalPurchase) { await this.submitExternal(); return; }
      const external = this.externalPurchase ? this.externalSelection() : null;
      const variant = external ? external.variant : this.activeVariant;
      const quantity = external ? external.quantity : this.quantity;
      if (!this.canBuy(variant, quantity)) { this.showError(variant?.available ? 'Please choose an available quantity.' : 'Please choose an available product option.'); return; }
      const planId = external ? external.planId : this.purchase === 'subscription' ? this.planId : '';
      const allocation = (variant.plans || []).find(plan => String(plan.id) === String(planId));
      if ((variant.requiresPlan && (!allocation || (!external && !this.root.querySelector('[data-fn-purchase]')))) || (planId && !allocation)) {
        this.showError('Please choose a purchase option in the offer above before adding to cart.');
        this.root.querySelector('#fn-offer')?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
        this.sync();
        return;
      }
      const item = { id: variant.id, quantity };
      if (allocation) item.selling_plan = allocation.id;
      const items = [item];
      this.root.querySelectorAll('[data-fn-upsell]:checked').forEach(input => { if (!input.disabled && /^\d+$/.test(input.value)) items.push({ id: Number(input.value), quantity: 1 }); });
      this.busy = true; this.showError('');
      this.root.querySelectorAll('[data-fn-add]').forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      try {
        const useDrawer = !this.root.dataset.cartType || this.root.dataset.cartType === 'drawer';
        const cart = useDrawer ? document.querySelector('cart-drawer') : null;
        const sections = typeof cart?.getSectionsToRender === 'function' ? cart.getSectionsToRender().map(section => section.id).filter(Boolean).slice(0, 5) : ['cart-icon-bubble'];
        let body, headers;
        if (external) {
          // Standard Shopify form fields retain app-supplied bundle items and line-item properties.
          body = external.fields;
          if (external.indexed) ['id', 'quantity', 'selling_plan'].forEach(name => body.delete(name));
          else {
            body.set('id', String(variant.id));
            body.set('quantity', String(quantity));
            if (planId) body.set('selling_plan', planId); else body.delete('selling_plan');
          }
          body.set('sections', sections.join(','));
          body.set('sections_url', window.location.pathname);
          headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
        } else {
          body = JSON.stringify({ items, sections, sections_url: window.location.pathname });
          headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
        }
        const response = await fetch(rootPath() + 'cart/add.js', { method: 'POST', headers, body });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'The product could not be added. Please try again.');
        let drawerOpened = false;
        if (typeof cart?.renderContents === 'function' && typeof cart?.open === 'function') {
          try {
            if (!sections.every(id => typeof result.sections?.[id] === 'string')) throw new Error('Cart sections unavailable');
            const state = { ...result, id: result.id || result.items?.[0]?.id };
            cart.renderContents(state, true);
            cart.classList.remove('is-empty');
            cart.open(this.lastStickyTrigger || event.submitter || document.activeElement);
            drawerOpened = true;
          } catch (_) { /* An added item is never added twice; use the actual cart if drawer rendering fails. */ }
        }
        if (!drawerOpened && result.sections?.['cart-icon-bubble']) {
          const parsed = new DOMParser().parseFromString(result.sections['cart-icon-bubble'], 'text/html');
          const fresh = parsed.querySelector('#cart-icon-bubble'), existing = document.querySelector('#cart-icon-bubble');
          if (fresh && existing) existing.innerHTML = fresh.innerHTML;
        }
        this.root.querySelectorAll('[data-fn-cart-status]').forEach(node => { node.hidden = false; node.textContent = 'Added to your cart.'; });
        this.root.querySelectorAll('[data-fn-cart-link]').forEach(node => { node.hidden = false; });
        document.dispatchEvent(new CustomEvent('fn:cart:updated', { detail: result }));
        if (!drawerOpened) {
          const cartUrl = this.root.dataset.cartUrl || window.routes?.cart_url || rootPath() + 'cart';
          window.location.assign(cartUrl);
        }
      } catch (error) {
        this.showError(error.message || 'Unable to add this item. Please check your connection and try again.');
      } finally {
        this.busy = false;
        this.lastStickyTrigger = null;
        this.root.querySelectorAll('[data-fn-add]').forEach(button => { button.disabled = !this.activeVariant?.available; button.removeAttribute('aria-busy'); });
      }
    }
    setupGallery() {
      this.track = this.root.querySelector('[data-fn-media-track]');
      this.slides = [...this.root.querySelectorAll('[data-fn-slide]')];
      this.galleryIndex = 0;
      let queued = false;
      this.listen(this.track, 'scroll', () => {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => { queued = false; if (!this.track) return; const first = this.track.getBoundingClientRect().left; let nearest = 0, distance = Infinity; this.slides.forEach((slide, index) => { const delta = Math.abs(slide.getBoundingClientRect().left - first); if (delta < distance) { distance = delta; nearest = index; } }); this.updateGallery(nearest); });
      }, { passive: true });
      this.updateGallery(0);
    }
    setupSticky() {
      const sticky = this.root.querySelector('[data-fn-sticky]');
      const primary = this.root.querySelector('.fn-product__buy [data-fn-add]');
      if (!sticky || !primary) return;
      const update = () => { sticky.hidden = primary.getBoundingClientRect().bottom > 0; };
      this.listen(window, 'scroll', update, { passive: true });
      this.listen(window, 'resize', update);
      update();
    }
    goTo(index, smooth = true) {
      if (!this.slides?.length) return;
      const next = (index + this.slides.length) % this.slides.length, slide = this.slides[next];
      this.track.scrollTo({ left: slide.offsetLeft - this.slides[0].offsetLeft, behavior: smooth && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto' });
      this.updateGallery(next);
    }
    updateGallery(index) {
      this.galleryIndex = index;
      this.text('[data-fn-gallery-count]', `${index + 1} / ${this.slides.length}`);
      this.root.querySelectorAll('[data-fn-thumb]').forEach(node => { node.setAttribute('aria-current', String(Number(node.dataset.fnThumb) === index)); });
      this.slides.forEach((slide, i) => { if (i !== index) slide.querySelectorAll('video').forEach(video => video.pause()); });
    }
    click(event) {
      const stickyAdd = event.target.closest('[data-fn-sticky-add]');
      if (stickyAdd) {
        event.preventDefault();
        // Reuse the primary button so native offers and attached bundle apps share one purchase path.
        const primary = this.root.querySelector('.fn-product__buy [data-fn-add]');
        if (primary && !primary.disabled && !this.busy) {
          this.lastStickyTrigger = stickyAdd;
          primary.click();
          if (!this.busy) this.lastStickyTrigger = null;
        }
        return;
      }
      const thumb = event.target.closest('[data-fn-thumb]'); if (thumb) this.goTo(Number(thumb.dataset.fnThumb));
      if (event.target.closest('[data-fn-gallery-prev]')) this.goTo(this.galleryIndex - 1);
      if (event.target.closest('[data-fn-gallery-next]')) this.goTo(this.galleryIndex + 1);
      const opener = event.target.closest('[data-fn-modal-open]');
      if (opener) { const dialog = document.getElementById(opener.dataset.fnModalOpen); if (dialog?.showModal) { this.lastOpener = opener; dialog.showModal(); dialog.querySelector('video')?.play().catch(() => {}); this.listen(dialog, 'close', () => { dialog.querySelectorAll('video').forEach(video => video.pause()); this.lastOpener?.focus(); }, { once: true }); } }
      if (event.target.closest('[data-fn-modal-close]')) event.target.closest('dialog')?.close();
      if (event.target.tagName === 'DIALOG') { const box = event.target.getBoundingClientRect(); if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) event.target.close(); }
    }
    destroy() { this.abort.abort(); this.root.querySelectorAll('video').forEach(video => video.pause()); }
  }
  const boot = scope => { scope.querySelectorAll('[data-fn-product]').forEach(root => { if (!roots.has(root)) roots.set(root, new FnProduct(root)); }); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document), { once: true }); else boot(document);
  document.addEventListener('shopify:section:load', event => boot(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-fn-product]').forEach(root => { roots.get(root)?.destroy(); roots.delete(root); }));
})();

/* Product-detail video cards. Included in fn-product.js for theme deployment. */
(() => {
  'use strict';
  const selector = '[data-fn-product-videos]';
  if (window.FNProductVideos) {
    window.FNProductVideos.init(document);
    return;
  }

  const cards = new Map();
  const motion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  const isHidden = () => document.visibilityState === 'hidden';
  const observer = window.IntersectionObserver ? new IntersectionObserver(entries => {
    entries.forEach(entry => cards.get(entry.target)?.visibility(entry.isIntersecting && entry.intersectionRatio >= 0.5));
  }, { threshold: [0, 0.5, 1] }) : null;

  function quietOthers(owner) {
    cards.forEach(card => { if (card !== owner) card.quiet(); });
  }

  function createCard(el) {
    const preview = el.querySelector('[data-fn-card-video]');
    const toggle = el.querySelector('[data-fn-video-toggle]');
    const previewToggle = el.querySelector('[data-fn-video-preview-toggle]');
    const sound = el.querySelector('[data-fn-video-sound]');
    const expand = el.querySelector('[data-fn-video-expand]');
    const dialog = el.querySelector('[data-fn-video-dialog]');
    const popup = dialog?.querySelector('[data-fn-dialog-video]');
    const close = dialog?.querySelector('[data-fn-video-close]');
    if (!preview) {
      if (toggle) { toggle.disabled = true; toggle.setAttribute('aria-label', 'Video unavailable'); }
      if (previewToggle) previewToggle.hidden = true;
      if (sound) sound.hidden = true;
      if (expand) expand.hidden = true;
      el.dataset.playing = 'false';
      return null;
    }
    const cfg = {
      autoplay: el.dataset.autoplay === 'true',
      muted: el.dataset.muted !== 'false',
      loop: el.dataset.loop === 'true',
      modal: el.dataset.playMode === 'modal' && !!popup
    };
    const removers = [];
    const requests = new Map();
    const desiredPlaying = new WeakMap();
    const pending = new Set();
    const seeks = new Map();
    const managedPauses = new WeakSet();
    const expectedMute = new WeakMap();
    // Without visibility observation, retain manual playback instead of autoplaying offscreen cards.
    let disposed = false, visible = false, userPaused = false, ended = false;
    let preference = null, open = false, autoFailed = false, returnFocus = null, wasPlaying = false, manualPlayback = false;
    const listen = (node, name, fn) => {
      if (!node) return;
      node.addEventListener(name, fn);
      removers.push(() => node.removeEventListener(name, fn));
    };
    const active = () => open && popup ? popup : preview;
    const playing = video => !video.paused && !video.ended;
    const canAuto = () => cfg.autoplay && !motion.matches && visible && !isHidden() && !userPaused && !ended && !open && !autoFailed;
    function update() {
      el.dataset.playing = String(playing(preview));
      el.dataset.muted = String(active().muted);
      el.dataset.ended = String(ended);
      el.dataset.dialogOpen = String(open);
      if (toggle) toggle.setAttribute('aria-label', cfg.modal ? 'Open video' : playing(preview) ? 'Pause video' : 'Play video');
      if (previewToggle) {
        previewToggle.setAttribute('aria-label', playing(preview) ? 'Pause preview' : 'Play preview');
        previewToggle.setAttribute('aria-pressed', String(playing(preview)));
      }
      if (sound) {
        sound.setAttribute('aria-label', active().muted ? 'Unmute video' : 'Mute video');
        sound.setAttribute('aria-pressed', String(!active().muted));
      }
    }
    function pause(video) {
      if (!video) return;
      requests.set(video, (requests.get(video) || 0) + 1);
      desiredPlaying.set(video, false);
      pending.delete(video);
      if (!video.paused) managedPauses.add(video);
      video.pause();
      update();
    }
    function applyMute(video, value) {
      if (!video) return;
      // volumechange is queued: record our assignment before its event can arrive.
      // A different observed value then identifies a native-control/user change.
      expectedMute.set(video, value);
      video.muted = value;
    }
    function muted(video, value) {
      if (!value) quietOthers(api);
      applyMute(video, value);
      update();
    }
    function seek(video, time) {
      if (!Number.isFinite(time) || time <= 0) return;
      if (video.readyState > 0) {
        try { video.currentTime = Number.isFinite(video.duration) ? Math.min(time, Math.max(0, video.duration - 0.05)) : time; } catch (_) { seeks.set(video, time); }
      } else seeks.set(video, time);
    }
    function start(video, automatic = false) {
      if (disposed || !video || pending.has(video) || (automatic && !canAuto())) return;
      if (!automatic) { autoFailed = false; ended = false; }
      if (video === preview) manualPlayback = !automatic;
      const request = (requests.get(video) || 0) + 1;
      requests.set(video, request);
      desiredPlaying.set(video, true);
      pending.add(video);
      el.dataset.videoError = 'false';
      let attempt;
      try { attempt = video.play(); } catch (error) { attempt = Promise.reject(error); }
      Promise.resolve(attempt).then(() => {
        if (disposed) { video.pause(); return; }
        // A newer play request owns the video now; never cancel it from an old promise.
        if (requests.get(video) !== request) {
          if (!desiredPlaying.get(video)) { video.pause(); update(); }
          return;
        }
        pending.delete(video);
        if (automatic && !canAuto()) pause(video);
        update();
      }).catch(() => {
        if (disposed || requests.get(video) !== request) return;
        pending.delete(video);
        if (automatic) autoFailed = true;
        el.dataset.videoError = 'true';
        pause(video);
      });
      update();
    }
    function autoplay() {
      if (canAuto() && !playing(preview) && !pending.has(preview)) {
        // Autoplay begins muted; only a deliberate user sound choice can override this.
        muted(preview, preference === null ? true : preference);
        start(preview, true);
      }
    }
    function finishDialog(restore = true) {
      if (!open) return;
      pause(popup);
      seek(preview, popup.currentTime);
      open = false;
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
      update();
      if (restore && !disposed) {
        const target = returnFocus?.isConnected && !returnFocus.disabled ? returnFocus : toggle;
        if (target?.isConnected) target.focus({ preventScroll: true });
      }
      if (wasPlaying) autoplay();
    }
    function openDialog() {
      if (!popup || !dialog || open || disposed) return;
      returnFocus = document.activeElement;
      wasPlaying = playing(preview) || pending.has(preview);
      pause(preview);
      open = true;
      userPaused = false;
      muted(popup, preference === null ? cfg.muted : preference);
      seek(popup, preview.currentTime);
      try {
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      } catch (_) { dialog.setAttribute('open', ''); }
      close?.focus({ preventScroll: true });
      start(popup);
      update();
    }
    function toggleInline() {
      if (playing(preview) || pending.has(preview)) {
        userPaused = true;
        pause(preview);
      } else {
        userPaused = false;
        if (ended || preview.ended) { try { preview.currentTime = 0; } catch (_) {} }
        muted(preview, preference === null ? cfg.muted : preference);
        start(preview);
      }
    }
    const api = {
      visibility(value) {
        visible = value;
        if (!visible) pause(preview);
        else autoplay();
      },
      quiet() {
        if (!preview.muted || (popup && !popup.muted)) {
          preference = true;
          applyMute(preview, true);
          applyMute(popup, true);
          update();
        }
      },
      environment() {
        if (isHidden()) { pause(preview); pause(popup); }
        else if (motion.matches) { if (cfg.autoplay && !manualPlayback) pause(preview); }
        else autoplay();
      },
      select() {
        if (open) finishDialog(false);
        pause(preview);
        el.scrollIntoView?.({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      },
      destroy() {
        if (disposed) return;
        disposed = true;
        finishDialog(false);
        pause(preview); pause(popup);
        observer?.unobserve(el);
        removers.forEach(remove => remove());
        seeks.clear(); pending.clear();
      }
    };
    [preview, popup].filter(Boolean).forEach(video => {
      video.autoplay = false; // Intersection visibility is the authority, not a browser autoplay attribute.
      video.removeAttribute('autoplay');
      video.loop = cfg.loop;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.defaultMuted = cfg.autoplay ? true : cfg.muted;
      applyMute(video, cfg.autoplay ? true : cfg.muted);
      listen(video, 'playing', () => { if (!video.muted) quietOthers(api); update(); });
      listen(video, 'pause', () => {
        if (managedPauses.has(video)) managedPauses.delete(video);
        else if (video === preview && visible && !isHidden() && !open && !video.ended) userPaused = true;
        update();
      });
      listen(video, 'ended', () => {
        if (!cfg.loop) { ended = true; userPaused = true; }
        update();
      });
      listen(video, 'volumechange', () => {
        if (expectedMute.get(video) !== video.muted) preference = video.muted;
        expectedMute.set(video, video.muted);
        if (!video.muted) quietOthers(api);
        update();
      });
      listen(video, 'loadedmetadata', () => {
        if (seeks.has(video)) { const time = seeks.get(video); seeks.delete(video); seek(video, time); }
      });
      listen(video, 'error', () => { autoFailed = true; el.dataset.videoError = 'true'; pause(video); });
    });
    listen(toggle, 'click', event => { event.preventDefault(); cfg.modal ? openDialog() : toggleInline(); });
    listen(previewToggle, 'click', event => { event.preventDefault(); toggleInline(); });
    listen(expand, 'click', event => { event.preventDefault(); openDialog(); });
    listen(sound, 'click', event => {
      event.preventDefault();
      preference = !active().muted;
      muted(active(), preference);
    });
    listen(close, 'click', event => { event.preventDefault(); finishDialog(); });
    listen(dialog, 'click', event => { if (event.target === dialog) finishDialog(); });
    listen(dialog, 'cancel', event => { event.preventDefault(); finishDialog(); });
    listen(dialog, 'close', () => { if (open) finishDialog(); });
    listen(dialog, 'keydown', event => {
      if (event.key === 'Escape' && open) { event.preventDefault(); finishDialog(); }
    });
    update();
    return api;
  }

  function init(root) {
    const nodes = [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
    nodes.forEach(el => {
      if (cards.has(el)) return;
      const card = createCard(el);
      if (!card) return;
      cards.set(el, card);
      if (observer) observer.observe(el);
      else card.environment();
    });
  }
  function destroy(root) {
    cards.forEach((card, el) => {
      if (root === el || root.contains(el)) { card.destroy(); cards.delete(el); }
    });
  }
  window.FNProductVideos = { init, destroy };
  document.addEventListener('visibilitychange', () => cards.forEach(card => card.environment()));
  const motionChanged = () => cards.forEach(card => card.environment());
  if (motion.addEventListener) motion.addEventListener('change', motionChanged);
  else if (motion.addListener) motion.addListener(motionChanged);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => destroy(event.target));
  document.addEventListener('shopify:block:select', event => {
    const selected = event.target.closest?.(selector) || event.target.querySelector?.(selector);
    if (selected) cards.get(selected)?.select();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
})();
